import { Close } from "@/components/Icons/utility/Close.tsx";
import { AvailabilityPopoverContent } from "@/components/Popover/AvailabilityPopoverContent.tsx";
import { PopoverContentProps } from "@/components/Popover/PopoverContentProps.ts";
import { CartAccommodationItems } from "@/components/ReservationSidebar/components/CartAccommodationItems.tsx";
import { ReservationSidebarProps } from "@/components/ReservationSidebar/ReservationSidebarProps.ts";
import { useReservationSidebarData } from "@/components/ReservationSidebar/useReservationSidebarData.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import format from "date-fns/format";
import { ArrowRight, Calendar, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover } from "react-tiny-popover";

export const ReservationSidebar = (props: ReservationSidebarProps) => {
  const isMobile = useIsMobile();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { containerEl } = useShadowRoot();

  const {
    defaultDateRange,
    cartAccommodationItems,
    calculateCartLoading,
    calculatedCartData,
    isCartItemInDisabledRateGroupFromRootAttributes,
  } = useReservationSidebarData(isMobile);
  const { t } = useTranslation();
  const { handleCloseCartDrawer } = useCartDrawerState();

  const contentRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const inner = contentInnerRef.current;
    const outer = contentRef.current;
    if (!inner || !outer) return;

    const getHeight = () => {
      const styles = getComputedStyle(outer);
      const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      return inner.offsetHeight + padding;
    };

    // Set initial height without transition
    outer.style.transition = "none";
    outer.style.height = `${getHeight()}px`;
    // Force reflow then enable transition
    outer.offsetHeight;
    outer.style.transition = "";

    const ro = new ResizeObserver(() => {
      outer.style.height = `${getHeight()}px`;
    });

    ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const isContinueDisabled = useMemo(() => {
    return (
      !cartAccommodationItems?.length ||
      props?.continueDisabled ||
      isCartItemInDisabledRateGroupFromRootAttributes ||
      !!calculatedCartData?.error
    );
  }, [
    cartAccommodationItems?.length,
    props?.continueDisabled,
    isCartItemInDisabledRateGroupFromRootAttributes,
    calculatedCartData?.error,
  ]);

  const renderTotalPrice = () => {
    let totalPrice = formatCurrency(0);
    if (calculatedCartData?.priceBreakdown?.withDiscount)
      totalPrice = formatCurrency(calculatedCartData.priceBreakdown?.withDiscount);
    if (calculatedCartData?.error) totalPrice = calculatedCartData?.error || t("error");
    if (cartAccommodationItems.length === 0) totalPrice = formatCurrency(0);

    return (
      <div className="c-reservation-sidebar__total">
        <span className="c-reservation-sidebar__total-label">{t("total")}</span>
        <div
          className={`c-reservation-sidebar__total-price ${
            calculatedCartData?.error ? "c-reservation-sidebar__total-error-price" : undefined
          }`}
        >
          <span className={calculateCartLoading ? "is-loading" : undefined}>{totalPrice}</span>
        </div>
      </div>
    );
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "\u2014";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "\u2014";
    return format(d, "dd.MM.yyyy");
  };

  const itemCount = cartAccommodationItems.length;
  const hasItems = itemCount > 0;

  return (
    <div className="c-reservation-sidebar-wrapper">
      <div className="c-reservation-sidebar-wrapper__close">
        <button className="c-close-button" onClick={handleCloseCartDrawer}>
          <Close />
        </button>
      </div>
      <div className={`c-reservation-sidebar ${!hasItems ? "is-empty" : ""}`}>
        <header className="c-reservation-sidebar__header">
          <div className="c-reservation-sidebar__header-top">
            <div className="c-reservation-sidebar__header-title">
              <div className="c-reservation-sidebar__header-icon">
                <ShoppingCart size={18} strokeWidth={2} />
                {hasItems && <span className="c-reservation-sidebar__badge">{itemCount}</span>}
              </div>
              <h5 className="u-h5 u-fw-600">{t("yourRes")}</h5>
            </div>
          </div>
          {!props.hideDatePicker && (
            <div className="c-reservation-sidebar__dates">
              <Calendar size={14} strokeWidth={2} />
              <span>{formatDate(defaultDateRange[0])}</span>
              <span className="c-reservation-sidebar__dates-separator" />
              <span>{formatDate(defaultDateRange[1])}</span>
            </div>
          )}
        </header>

        <div className="c-reservation-sidebar__content" ref={contentRef}>
          <div ref={contentInnerRef}>
            {!hasItems ? (
              <div className="c-reservation-sidebar__blank-state">
                <div className="c-reservation-sidebar__blank-state-icon">
                  <ShoppingCart size={28} strokeWidth={1.5} />
                </div>
                <p>{t("noAccommodationSelected")}</p>
              </div>
            ) : (
              <CartAccommodationItems hideRemoveRate={props.hideRemoveRate} isReserved={props.isReserved} />
            )}
          </div>
        </div>

        <footer className="c-reservation-sidebar__footer">
          {renderTotalPrice()}
          {!props.isReserved ? (
            <Popover
              isOpen={popoverOpen}
              parentElement={containerEl}
              content={({ position, childRect, popoverRect }: PopoverContentProps) => (
                <AvailabilityPopoverContent
                  popoverRect={popoverRect}
                  childRect={childRect}
                  position={position}
                  text={props.continueBtnPopoverText}
                />
              )}
            >
              <button
                className="c-be-button"
                disabled={isContinueDisabled}
                onClick={props.onContinue}
                onMouseOver={() => props.continueBtnPopoverText && setPopoverOpen(true)}
                onMouseLeave={() => props.continueBtnPopoverText && setPopoverOpen(false)}
              >
                <span className="c-be-button__label">
                  {props.continueButtonText
                    ? props.continueButtonText
                    : isCartItemInDisabledRateGroupFromRootAttributes
                    ? t("inquiryOnly")
                    : t("continueWithRes")}
                </span>
                <span className="c-be-button__icon">
                  <ArrowRight size={18} strokeWidth={2.5} />
                </span>
              </button>
            </Popover>
          ) : null}
        </footer>
      </div>
    </div>
  );
};
